import { Download, Eye, File as FileIcon, FileText, ImageOff, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { FilePreviewDialog } from '@/components/files/FilePreviewDialog'
import { Button } from '@/components/ui/button'
import { useFileMetadata } from '@/hooks/useFileUpload'
import type { FileMetadata } from '@/types/api/file'
import { cn } from '@/utils/cn'
import { fileNameFromKey, formatBytes, isImageFile, isPdfFile, saveFile } from '@/utils/file-utils'

export interface FilePreviewCardProps {
  /** S3 key of the stored file. */
  fileKey: string
  /** Known metadata (upload response / activity snapshot). Loaded via `metadata` when absent. */
  name?: string | null
  sizeBytes?: number | null
  contentType?: string | null
  /** Pre-signed URL that displays the file. When absent the card falls back to `metadata.downloadUrl`. */
  downloadUrl?: string | null
  /** Pre-signed URL that saves the file under its name. When absent the card looks the file up. */
  attachmentUrl?: string | null
  /** Batch metadata lookup result for this key (from `useFileMetadata`). */
  metadata?: FileMetadata | null
  /** Shows the loading shimmer while metadata is being fetched. */
  isLoading?: boolean
  /** Optional remove/replace action (e.g. clearing a record value). */
  onRemove?: () => void
  removeLabel?: string
  /** Opens the preview elsewhere (a field's shared viewer, so its files can be stepped through). Without it the card shows its own. */
  onPreview?: () => void
  className?: string
}

/**
 * Reusable preview for an uploaded file: image thumbnail (or PDF/generic icon),
 * file name, size and a download button. Images and PDFs open in a viewer dialog; downloading saves
 * the file in place, so neither ever opens a tab showing the storage address.
 */
export function FilePreviewCard({
  fileKey,
  name,
  sizeBytes,
  contentType,
  downloadUrl,
  attachmentUrl,
  metadata,
  isLoading = false,
  onRemove,
  removeLabel = 'Remove',
  onPreview,
  className,
}: FilePreviewCardProps) {
  const [previewOpen, setPreviewOpen] = useState(false)

  // Fetch metadata (+ signed URLs) when the caller has none — e.g. activity snapshots. While the
  // caller is still loading it (isLoading), wait for theirs rather than asking twice.
  const { data: fetchedMetadata, isLoading: isFetchingMetadata } = useFileMetadata(
    !isLoading && !metadata && !(downloadUrl && attachmentUrl) && fileKey ? [fileKey] : []
  )
  const resolvedMetadata = metadata ?? fetchedMetadata?.find((entry) => entry.key === fileKey) ?? null

  const resolvedName = name ?? resolvedMetadata?.name ?? fileNameFromKey(fileKey)
  const resolvedSize = sizeBytes ?? resolvedMetadata?.sizeBytes ?? null
  const resolvedType = contentType ?? resolvedMetadata?.contentType ?? null
  const resolvedUrl = downloadUrl ?? resolvedMetadata?.downloadUrl ?? null
  const resolvedAttachmentUrl = attachmentUrl ?? resolvedMetadata?.attachmentUrl ?? null
  const loading = isLoading || isFetchingMetadata

  const isImage = isImageFile(resolvedType, resolvedName)
  const isPdf = isPdfFile(resolvedType, resolvedName)

  const icon = isPdf
    ? <FileText className="h-5 w-5 text-rose-500" />
    : <FileIcon className="h-5 w-5 text-neutral-400" />

  const canPreview = (isImage || isPdf) && !!resolvedUrl

  const download = () => saveFile(resolvedAttachmentUrl, resolvedUrl)
  const openPreview = () => (onPreview ? onPreview() : setPreviewOpen(true))

  return (
    <div className={cn('rounded-lg border border-neutral-200 bg-white p-2', className)}>
      <div className="flex items-start gap-2.5">
        {/* Thumbnail / icon */}
        {isImage && resolvedUrl ? (
          <button
            type="button"
            onClick={openPreview}
            className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-neutral-100 bg-neutral-50"
            title="Preview image"
          >
            <img src={resolvedUrl} alt={resolvedName} className="h-full w-full object-cover" />
          </button>
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-neutral-100 bg-neutral-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-neutral-300" /> : icon}
          </div>
        )}

        {/* Name + size */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-neutral-800" title={resolvedName}>
            {resolvedName}
          </p>
          <p className="text-xs text-neutral-400">
            {resolvedSize != null ? formatBytes(resolvedSize) : loading ? 'Loading…' : '—'}
            {resolvedType ? ` · ${resolvedType}` : ''}
          </p>
          {!resolvedUrl && !loading && (
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600">
              <ImageOff className="h-3 w-3" /> Preview link unavailable
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1">
          {(isImage || isPdf) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={!canPreview}
              onClick={openPreview}
              title={isImage ? 'Preview image' : 'Preview PDF'}
              aria-label={`Preview ${resolvedName}`}
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={!resolvedAttachmentUrl && !resolvedUrl}
            onClick={download}
            title="Download file"
            aria-label={`Download ${resolvedName}`}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-destructive-600 hover:text-destructive-700"
              onClick={onRemove}
            >
              {removeLabel}
            </Button>
          )}
        </div>
      </div>

      {!onPreview && (
        <FilePreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          index={0}
          onIndexChange={() => {}}
          files={[{ key: fileKey, name: resolvedName, url: resolvedUrl, attachmentUrl: resolvedAttachmentUrl, contentType: resolvedType }]}
        />
      )}
    </div>
  )
}
